import React, { useState } from 'react';
import { IFile } from '@routes/Common/type';
import useFiles from '@hooks/useFiles';
import Loading from '@components/Loading';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import { RfqDetailData } from '@routes/Common/type';
import { Box, makeStyles } from '@material-ui/core';
import { Download } from '@carbon/icons-react';
import ReferenceFileList from './ReferenceFileList';

interface UseStylesProps {
  matches?: boolean;
}

const useStyles = makeStyles(() => {
  return {
    boxTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: `16px 0 16px 0`,
      '& svg': {
        fontSize: '20px !important'
      }
    },
    displayItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px',
      '& .MuiTypography-colorTextSecondary': {
        color: colorSet.gray600
      },
      width: (props: UseStylesProps) => (props.matches ? '100%' : 272),
      height: 77,
      border: `1px solid ${colorSet.gray400}`,
      borderRadius: 12,
      '&:hover': {
        cursor: 'pointer',
        '& button': {
          background: colorSet.gray100
        }
      }
    }
  };
});

interface Props {
  data?: RfqDetailData;
  matches?: boolean;
}

const RfqReferenceImagePresenter: React.FC<Props> = ({ data, matches }) => {
  const classes = useStyles({ matches: matches });
  const { enqueueSnackbar } = useSnackbar();
  const { onDownloadFile, onDownloadZipFile } = useFiles();
  const [isLoading, setIsLoading] = useState(false);
  const referenceFiles = data?.referenceFiles ?? [];
  const referenceImageFiles = referenceFiles;

  const onFileDownLoad = (file: IFile) => {
    onDownloadFile(file)
      .then((resp) => {
        if (!resp) {
          enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
            variant: 'error'
          });
        }
      })
      .catch(() => {
        enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
          variant: 'error'
        });
      });
  };

  const onFileDownloadAll = () => {
    if (referenceImageFiles.length < 1) return false;

    const fileArr: Array<IFile> = [];
    setIsLoading(true);

    referenceImageFiles.map((file) => {
      if (file.fileId && file.fileName) {
        fileArr.push({ fileId: file.fileId, fileName: file.fileName });
      }
      return null;
    });

    if (fileArr.length > 1) {
      onDownloadZipFile(fileArr)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
            variant: 'error'
          });
        });
    } else if (fileArr.length === 1) {
      setIsLoading(false);
      onFileDownLoad(fileArr[0]);
    }
  };

  return (
    <React.Fragment>
      <Loading open={isLoading} />
      <Box className="item">
        <div className={classes.boxTitle}>
          <CustomizedTypography
            variant={'h4'}
            color="textPrimary"
            fontWeight="500"
          >
            참고 파일
          </CustomizedTypography>
          {referenceImageFiles.length > 0 && (
            <CustomizedButton
              variant="text"
              size="large"
              startIcon={<Download />}
              onClick={onFileDownloadAll}
            >
              전체 다운로드
            </CustomizedButton>
          )}
        </div>
        {referenceImageFiles.length > 0 ? (
          <ReferenceFileList files={referenceImageFiles} rfqId={data?.id} />
        ) : (
          <CustomizedTypography
            variant={matches ? 'body2' : 'body1'}
            color="textSecondary"
          >
            참고한 이미지 파일이 없습니다.
          </CustomizedTypography>
        )}
      </Box>
    </React.Fragment>
  );
};

export default RfqReferenceImagePresenter;
